/**
 * TopMentionsPage — Universe › Top Mentions (bd deepcontext-d68.9, restyled
 * per the locked mockup — bd deepcontext-4dyp; stripped chrome + in-canvas
 * detail card — bd deepcontext-6l96.2).
 *
 * Force-directed bubble graph of the most-mentioned entities in a market.
 *   - Bubble size scales with mention count (sqrt scale).
 *   - Bubble fill is the shared gradient/shadow bubble style (bd
 *     deepcontext-ulrb) — the same primitives ClustersGraph draws. The
 *     segmented sentiment ring (NOT a pie fill) renders only for the
 *     hovered or selected bubble (bd deepcontext-6l96.2) — with 24 bubbles
 *     on screen by default, a ring on every bubble at once read as a wall of
 *     green outlines. Ring/rail colours all come from the theme's status
 *     triad (chartTheme.ts): safe-green positive, muted-gray neutral, risk-red
 *     negative.
 *   - Density (bd deepcontext-6l96.2): default limit dropped from 50 to 24,
 *     physics matched to ClustersGraph's charge/centring forces, and the
 *     collide radius sized via the shared `bubbleCollideRadius` helper so a
 *     neighbouring bubble's ring — or below-bubble label pill — can never
 *     overlap this one's.
 *   - Chrome (bd deepcontext-6l96.2): the Universe kicker, page title,
 *     subtitle, nested Card, sentiment legend, top-right size/interaction
 *     overlay, and Pan/+/-/Reset button cluster are gone. One Clusters-style
 *     hint line (top-left) and the Size-by toggle (bottom-right) remain. The
 *     shown/total entity and mention counts portal into the shared
 *     `#app-page-actions` row, left of the market selector (mirroring
 *     AnchorBar's order-first/order-last technique — see ClustersTopBar).
 *   - Clicking a bubble opens `MentionDetailPanel` as an in-canvas overlay
 *     anchored beside it, mirroring how ClustersGraph anchors
 *     `TopicDetailPanel` beside its own selected bubble.
 *
 * Ported from the vytas-deepcontext-clusters source repo `BrandMentions.jsx`
 * but renamed "Top Mentions" because the underlying entity column mixes real
 * brands, people, and generic proper nouns — calling it "Brand Mentions" was
 * misleading.
 *
 * The d3 force simulation mirrors the source repo's physics. d3-* sub-packages
 * are stubbed in the shared Universe Jest setup so this component renders in
 * tests without spinning up the actual simulation.
 */

import React, {
    useCallback,
    useContext,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from 'react';
import {createPortal} from 'react-dom';
import {
    forceCollide,
    forceManyBody,
    forceSimulation,
    forceX,
    forceY,
    Simulation,
} from 'd3-force';
import {select} from 'd3-selection';
import {zoom, zoomIdentity, ZoomTransform} from 'd3-zoom';
import {useChartPalette} from '../../components/charts/useChartMode';
import {
    BubbleCircle,
    BubbleDefs,
    BubbleLabel,
    BubbleSelectionRing,
    BubbleSentimentRing,
    bubbleCollideRadius,
} from '../../components/charts/bubbleStyle';
import {getMapPanelStyle} from '../../components/charts/mapPanelStyle';
import {
    isAbortError,
    extractApiError,
    clustersApi,
    TopMentionEntity,
    TopMentionsPayload,
} from '../../api/clusters';
import MentionDetailPanel from './components/MentionDetailPanel';
import ClustersMarketContext from './contexts/ClustersMarketContext';
import {usePortalSlot} from '../../hooks/usePortalSlot';

const DEFAULT_LIMIT = 24;
const MIN_R = 22;
const MAX_R = 78;

/**
 * Bubble-size metric. Mirrors the Universe "Size by" toggle so both bubble
 * views behave consistently (bd deepcontext-3wiv1). `pages` sizes by mention
 * `count`, `impressions` by `total_impressions`; both feed the same
 * `radiusFor()` sqrt scale. Default is `pages`.
 */
type SizeBy = 'pages' | 'impressions';

const SIZE_OPTIONS: Array<{key: SizeBy; label: string}> = [
    {key: 'pages', label: 'Pages'},
    {key: 'impressions', label: 'Impressions'},
];

interface TopMentionsPageProps {
    /**
     * Override the context-provided market. Production code leaves this
     * undefined and reads `selectedMarket` from `ClustersMarketContext`; tests
     * pass an explicit code to avoid having to mount the provider.
     */
    market?: string;
    /** Override the default limit (tests). */
    limit?: number;
}

interface BubbleNode {
    id: string;
    data: TopMentionEntity;
    r: number;
    x: number;
    y: number;
    vx?: number;
    vy?: number;
    fx?: number | null;
    fy?: number | null;
    /** Stable index into the theme's categorical palette (bd deepcontext-fgvn.5). */
    paletteIndex: number;
}

function radiusFor(count: number, maxCount: number): number {
    const safeMax = Math.max(maxCount, 1);
    const t = Math.sqrt(Math.max(count, 0)) / Math.sqrt(safeMax);
    return MIN_R + (MAX_R - MIN_R) * t;
}

// Stable string-hash -> index, same algorithm ClustersGraph uses to assign a
// bubble its gradient slot when no explicit palette hue is supplied — so a
// given entity id always paints the same colour across renders.
function hashIndex(key: string, len: number): number {
    let hash = 0;
    for (let i = 0; i < key.length; i += 1) {
        hash = (hash * 31 + key.charCodeAt(i)) | 0;
    }
    return Math.abs(hash) % len;
}

/**
 * Sentiment breakdown as fractions (0..1) of the entity's total, in the
 * positive/neutral/negative shape `BubbleSentimentRing` expects (bd
 * deepcontext-ulrb). An entity with no sentiment signal at all renders no
 * ring segments (all fractions 0) rather than a colour that isn't backed by
 * data.
 */
function sentimentFractions(entity: TopMentionEntity): {
    positive: number;
    neutral: number;
    negative: number;
} {
    const {negative, neutral, positive} = entity.sentiment_breakdown;
    const total = negative + neutral + positive;
    if (total <= 0) return {positive: 0, neutral: 0, negative: 0};
    return {positive: positive / total, neutral: neutral / total, negative: negative / total};
}

/** Shown/total counts, portalled into the shared page-actions row (bd
 * deepcontext-6l96.2). `order-first` pins this ahead of MarketSelector /
 * UrlCountBadge (portalled separately by `ClustersTopBar`) regardless of
 * which one mounts into the slot first — the same technique AnchorBar uses
 * for its own `order-last` action button. */
const CountChip: React.FC<{kicker: string; value: string}> = ({kicker, value}) => (
    <span
        data-testid={`top-mentions-count-${kicker.toLowerCase()}`}
        className="inline-flex items-center h-[42px] gap-1.5 rounded-full border border-line bg-surface px-3 text-xs whitespace-nowrap"
    >
        <span className="text-[10px] font-bold uppercase tracking-wider text-ink-muted">
            {kicker}
        </span>
        <span className="font-extrabold text-ink">{value}</span>
    </span>
);

const TopMentionsPage: React.FC<TopMentionsPageProps> = ({
    market: marketProp,
    limit = DEFAULT_LIMIT,
}) => {
    const ctx = useContext(ClustersMarketContext);
    const market = marketProp ?? ctx?.selectedMarket ?? null;
    // Theme-aware categorical + status palette (bd deepcontext-fgvn.5) — the
    // ring and rail both read the same `status.{good,neutral,critical}`
    // triad so a light/dark toggle repaints every sentiment colour together.
    const palette = useChartPalette();
    const sentimentColors = useMemo(
        () => ({
            positive: palette.status.good,
            neutral: palette.status.neutral,
            negative: palette.status.critical,
        }),
        [palette.status.good, palette.status.neutral, palette.status.critical],
    );
    // Namespaces the shared bubble-style module's gradient/filter ids (bd
    // deepcontext-ulrb) so this bubble graph never collides with ClustersGraph's
    // <defs> if both ever mount in the same tree.
    const bubbleIdPrefix = useId();
    const actionsSlot = usePortalSlot('app-page-actions');
    const [payload, setPayload] = useState<TopMentionsPayload | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [hoveredId, setHoveredId] = useState<string | null>(null);
    const [sizeBy, setSizeBy] = useState<SizeBy>('pages');
    const [size, setSize] = useState<{w: number; h: number}>({w: 1000, h: 560});

    const containerRef = useRef<HTMLDivElement | null>(null);
    const svgRef = useRef<SVGSVGElement | null>(null);
    const simRef = useRef<Simulation<BubbleNode, undefined> | null>(null);
    const nodesRef = useRef<BubbleNode[]>([]);
    const zoomBehaviorRef = useRef<ReturnType<typeof zoom<Element, unknown>> | null>(
        null,
    );
    const [, tick] = useState(0);
    const [isPanning, setIsPanning] = useState(false);
    const [transform, setTransform] = useState<ZoomTransform>(zoomIdentity);

    // Cancel the mentions fetch on tab switch / unmount (bd deepcontext-26s).
    useEffect(() => {
        if (!market) {
            setLoading(true);
            return;
        }
        const controller = new AbortController();
        setLoading(true);
        setLoadError(null);
        clustersApi
            .getTopMentions(market, limit, {signal: controller.signal})
            .then((data) => {
                if (controller.signal.aborted) return;
                setPayload(data);
            })
            .catch((err: unknown) => {
                if (isAbortError(err) || controller.signal.aborted) return;
                setLoadError(extractApiError(err, 'Failed to load top mentions.'));
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });
        return () => {
            controller.abort();
        };
    }, [market, limit]);

    // Reset selection when the market changes.
    useEffect(() => {
        setSelectedId(null);
    }, [market]);

    const initial = useMemo<BubbleNode[]>(() => {
        const entities = payload?.entities ?? [];
        const metric = (e: TopMentionEntity) =>
            sizeBy === 'impressions' ? e.total_impressions : e.count;
        const maxMetric = entities.reduce((m, e) => Math.max(m, metric(e)), 1);
        const paletteLen = palette.categorical.length;
        return entities.map((e, i) => ({
            id: e.id,
            data: e,
            r: radiusFor(metric(e), maxMetric),
            x: size.w / 2 + Math.cos(i * 0.55) * (60 + ((i * 8) % 200)),
            y: size.h / 2 + Math.sin(i * 0.55) * (60 + ((i * 8) % 200)),
            paletteIndex: hashIndex(e.id, paletteLen),
        }));
    }, [payload, size.w, size.h, sizeBy, palette.categorical.length]);

    // Physics matched to ClustersGraph.tsx's charge/centring forces (bd
    // deepcontext-6l96.2) so both Universe bubble graphs settle with the same
    // feel. The collide radius is NOT ClustersGraph's `r + 3` — it's sized via
    // the shared `bubbleCollideRadius` helper so a neighbour's sentiment ring
    // (and, for small/long-labelled bubbles, its below-bubble pill) can never
    // overlap this bubble's own.
    useEffect(() => {
        nodesRef.current = initial;
        tick((x) => x + 1);
        if (initial.length === 0) {
            simRef.current?.stop();
            simRef.current = null;
            return undefined;
        }
        const sim = forceSimulation<BubbleNode>(initial)
            .force(
                'collide',
                forceCollide<BubbleNode>((d) =>
                    bubbleCollideRadius(d.r, d.data.label, d.data.count.toLocaleString(), true),
                ),
            )
            .force('charge', forceManyBody<BubbleNode>().strength(-180))
            .force('cx', forceX<BubbleNode>(size.w / 2).strength(0.05))
            .force('cy', forceY<BubbleNode>(size.h / 2).strength(0.05))
            .alpha(0.9)
            .alphaDecay(0.018)
            .on('tick', () => tick((x) => x + 1));
        simRef.current = sim;
        return () => {
            sim.stop();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initial]);

    useEffect(() => {
        const sim = simRef.current;
        if (!sim) return;
        sim.force('cx', forceX<BubbleNode>(size.w / 2).strength(0.05));
        sim.force('cy', forceY<BubbleNode>(size.h / 2).strength(0.05));
        sim.alpha(0.4).restart();
    }, [size.w, size.h]);

    useEffect(() => {
        const el = containerRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver((entries) => {
            for (const e of entries) {
                const r = e.contentRect;
                if (r.width > 0 && r.height > 0) {
                    setSize({w: r.width, h: r.height});
                }
            }
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    // d3-zoom for pan + scroll-wheel zoom. Mirrors ClustersGraph.tsx's own
    // zoom filter (bd deepcontext-6l96.2 dropped the explicit Pan-mode toggle
    // — dragging empty canvas already pans regardless of it): pan starts on
    // the SVG background, with Shift or middle/right-click; scroll wheel
    // zooms. `entities.length` is a dep so the effect rebinds when the SVG
    // (which is gated behind the loading/empty branches) actually mounts.
    useEffect(() => {
        if (!svgRef.current) return undefined;
        const svg = select(svgRef.current as Element);
        const z = zoom<Element, unknown>()
            .scaleExtent([0.3, 4])
            .filter((event: Event) => {
                const mouseEvent = event as MouseEvent & {shiftKey: boolean};
                if (event.type === 'wheel') return true;
                if (mouseEvent.button === 1 || mouseEvent.button === 2) return true;
                if (mouseEvent.shiftKey) return true;
                const target = event.target as Element | null;
                return target?.tagName === 'svg';
            })
            .on('start', () => setIsPanning(true))
            .on('zoom', (event: {transform: ZoomTransform}) =>
                setTransform(event.transform),
            )
            .on('end', () => setIsPanning(false));
        svg.call(z as never);
        zoomBehaviorRef.current = z;
        const ctxHandler = (e: Event) => e.preventDefault();
        svgRef.current.addEventListener('contextmenu', ctxHandler);
        const node = svgRef.current;
        return () => {
            svg.on('.zoom', null);
            node.removeEventListener('contextmenu', ctxHandler);
        };
    }, [payload?.entities?.length ?? 0]);

    const handleSelect = useCallback((id: string) => {
        setSelectedId((prev) => (prev === id ? prev : id));
    }, []);

    const handleClose = useCallback(() => setSelectedId(null), []);

    const sentimentLabels = payload?.sentiment_labels ?? {1: 'Negative', 2: 'Neutral', 3: 'Positive'};
    const selectedEntity =
        payload?.entities.find((e) => e.id === selectedId) ?? null;
    const entities = payload?.entities ?? [];
    const selectedNode = useMemo(
        () => (selectedId != null ? nodesRef.current.find((n) => n.id === selectedId) ?? null : null),
        // nodesRef mutates in place on every tick; re-derive whenever the tick
        // counter or selection changes rather than relying on nodesRef itself
        // as a dependency (it never changes identity).
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedId, nodesRef.current],
    );

    return (
        <div data-testid="top-mentions-page" className="flex flex-col h-full min-h-0 gap-3">
            {actionsSlot &&
                entities.length > 0 &&
                createPortal(
                    <div className="order-first flex items-center gap-2" data-testid="top-mentions-counts">
                        <CountChip kicker="Entities" value={entities.length.toLocaleString()} />
                        <CountChip
                            kicker="Mentions"
                            value={entities.reduce((s, e) => s + e.count, 0).toLocaleString()}
                        />
                    </div>,
                    actionsSlot,
                )}

            {loading ? (
                <div
                    data-testid="top-mentions-loading"
                    className="px-4 py-12 text-sm text-ink-muted text-center"
                >
                    Loading Top Mentions…
                </div>
            ) : loadError ? (
                <div
                    data-testid="top-mentions-error"
                    className="px-4 py-12 text-sm text-risk text-center"
                >
                    {loadError}
                </div>
            ) : entities.length === 0 ? (
                <div
                    data-testid="top-mentions-empty"
                    className="px-4 py-12 text-sm text-ink-muted text-center"
                >
                    No mentions available for market {market ?? '—'}.
                </div>
            ) : (
                <div
                    ref={containerRef}
                    data-testid="top-mentions-graph"
                    className="relative flex-1 min-h-0 w-full rounded-2xl overflow-hidden"
                    style={{
                        height: 560,
                        cursor: isPanning ? 'grabbing' : 'default',
                        ...getMapPanelStyle(palette.mode),
                    }}
                >
                    <svg
                        ref={svgRef}
                        width="100%"
                        height="100%"
                        viewBox={`0 0 ${size.w} ${size.h}`}
                        preserveAspectRatio="xMidYMid meet"
                        style={{position: 'absolute', inset: 0}}
                    >
                        <BubbleDefs idPrefix={bubbleIdPrefix} palette={palette} />
                        <g
                            data-testid="top-mentions-zoom-layer"
                            transform={`translate(${transform.x},${transform.y}) scale(${transform.k})`}
                        >
                        {nodesRef.current.map((n) => {
                            const isSel = n.id === selectedId;
                            const isHovered = n.id === hoveredId;
                            const showRing = isSel || isHovered;
                            const sb = n.data.sentiment_breakdown;
                            const sbTotal = sb.negative + sb.neutral + sb.positive;
                            return (
                                <g
                                    key={n.id}
                                    data-testid="top-mentions-bubble"
                                    data-entity-id={n.id}
                                    data-r={n.r.toFixed(2)}
                                    data-sentiment-total={sbTotal}
                                    data-sentiment-positive={sb.positive}
                                    data-sentiment-neutral={sb.neutral}
                                    data-sentiment-negative={sb.negative}
                                    transform={`translate(${n.x || 0},${n.y || 0})`}
                                    style={{cursor: 'pointer'}}
                                    onClick={() => handleSelect(n.id)}
                                    onMouseEnter={() => setHoveredId(n.id)}
                                    onMouseLeave={() =>
                                        setHoveredId((h) => (h === n.id ? null : h))
                                    }
                                >
                                    <BubbleCircle
                                        r={n.r}
                                        paletteIndex={n.paletteIndex % palette.categorical.length}
                                        idPrefix={bubbleIdPrefix}
                                    />
                                    {showRing && (
                                        <BubbleSentimentRing
                                            r={n.r}
                                            breakdown={sentimentFractions(n.data)}
                                            colors={sentimentColors}
                                        />
                                    )}
                                    {isSel && (
                                        <BubbleSelectionRing r={n.r} stroke={palette.chrome.ink} />
                                    )}
                                    <BubbleLabel
                                        r={n.r}
                                        label={n.data.label}
                                        subLabel={n.data.count.toLocaleString()}
                                        palette={palette}
                                        fitLabel
                                    />
                                </g>
                            );
                        })}
                        </g>
                    </svg>
                    <div className="absolute left-3 top-3 z-10 text-xs text-ink-muted font-medium leading-tight">
                        Drag empty space to pan · scroll to zoom · click a bubble for its mentions
                    </div>
                    <div className="absolute right-3 bottom-3 z-10 flex items-center gap-1.5">
                        <span className="text-xs font-medium text-ink-muted">Size by</span>
                        <div
                            data-testid="top-mentions-size-toggle"
                            role="group"
                            aria-label="Size by"
                            className="inline-flex items-center rounded-full bg-ghost-bg p-1 h-[42px] text-xs font-semibold"
                        >
                            {SIZE_OPTIONS.map((opt) => {
                                const active = opt.key === sizeBy;
                                return (
                                    <button
                                        key={opt.key}
                                        type="button"
                                        onClick={() => setSizeBy(opt.key)}
                                        aria-pressed={active}
                                        data-size-option={opt.key}
                                        className={`h-full rounded-full px-3 transition-colors ${
                                            active
                                                ? 'bg-surface text-ink shadow-sm'
                                                : 'text-ink-muted hover:text-ink'
                                        }`}
                                    >
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* bd deepcontext-6l96.2: the right-rail aside is gone —
                        clicking a bubble opens its detail card as an
                        absolutely-positioned overlay anchored beside it,
                        inside the canvas itself, mirroring how ClustersGraph
                        anchors TopicDetailPanel beside its own selected
                        bubble. */}
                    {selectedEntity &&
                        selectedNode &&
                        (() => {
                            const cx = transform.x + selectedNode.x * transform.k;
                            const cy = transform.y + selectedNode.y * transform.k;
                            const r = selectedNode.r * transform.k;
                            const panelWidth = 320;
                            const panelHeight = 420;
                            const overflowsRight = cx + r + 16 + panelWidth > size.w;
                            const left = overflowsRight
                                ? Math.max(8, cx - r - 16 - panelWidth)
                                : Math.min(size.w - panelWidth - 8, cx + r + 16);
                            const top = Math.max(
                                8,
                                Math.min(size.h - panelHeight - 8, cy - 120),
                            );
                            return (
                                <div
                                    data-testid="mention-detail-overlay"
                                    className="absolute z-20"
                                    style={{left, top, width: panelWidth}}
                                >
                                    <MentionDetailPanel
                                        entity={selectedEntity}
                                        sentimentLabels={sentimentLabels}
                                        colors={sentimentColors}
                                        onClose={handleClose}
                                    />
                                </div>
                            );
                        })()}
                </div>
            )}
        </div>
    );
};

export default TopMentionsPage;
